import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_LIMIT, ENDPOINTS, type UnresolvedObligationItem } from '@clm/shared-types';

import { fetchObligationOwners } from '../../api/quality';
import { ContractTypeTag } from '../../components/ContractTypeTag';
import { CursorPager } from '../../components/CursorPager';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { PanelProblem, Tile } from './panel';
import { usePaging, usePanel } from './panel-state';

/**
 * (c) Unresolved obligation owners — O-1's failure shape, made visible.
 *
 * An occurrence with no recipient is `notify` exiting 1: a missed date with a
 * green dashboard. The fix is a confirmed `role_mapping` row or a
 * `contract_owner_upn` on the contract. Everything here is **scoped** on
 * `root_contract_key` — totals and rows alike — so a practice-scoped PMO sees
 * exactly the obligations they can fix. Every role holds `contract:read`, so a
 * 403 is never expected here; an empty page means nothing visible has a problem.
 *
 * No action: fixing an owner is `contract:update` on the contract record, or a
 * `role_mapping` row, for which no endpoint exists yet.
 */

const PANEL = 'Unresolved obligation owners';
const ENDPOINT_PATH = ENDPOINTS['quality-obligation-owners'].path;

const COLUMNS: readonly DataTableColumn<UnresolvedObligationItem>[] = [
  { id: 'due', header: 'Due', cell: (o) => o.due_date ?? 'undated' },
  {
    id: 'contract',
    header: 'Contract',
    cell: (o) => <Link to={`/contracts/record/${encodeURIComponent(o.contract_key)}`}>{o.contract_key}</Link>,
  },
  /* CLM-110 (R-110-23) — provisional until an administrator confirms it. */
  { id: 'type', header: 'Type', cell: (o) => <ContractTypeTag type={o.contract_type} confirmation={o.contract_type_confirmation} /> },
  { id: 'description', header: 'Obligation', cell: (o) => o.description },
  { id: 'status', header: 'Status', cell: (o) => o.obligation_status },
  { id: 'role', header: 'Owner role in text', cell: (o) => o.owner_role_text ?? '—' },
  {
    id: 'owner',
    header: 'Contract owner',
    cell: (o) => (o.contract_owner_upn === null ? <strong>none</strong> : o.contract_owner_upn),
  },
];

export function ObligationOwnersPanel() {
  const paging = usePaging();
  const { state, reload } = usePanel(
    (abort) =>
      fetchObligationOwners(
        { page: paging.cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor: paging.cursor } },
        abort,
      ),
    [paging.cursor],
  );

  return (
    <section className="card qpanel" aria-labelledby="q-owners">
      <h3 id="q-owners">{PANEL}</h3>
      <p>
        Open obligations whose owner could not be resolved to a person or a group, on contracts in your scope.
        A reminder for one of these has nobody to go to. Fix it on the contract record (an owner) or with a
        confirmed role mapping; the three mappings the notifier resolves through are shown below.
      </p>

      {state.kind === 'loading' ? (
        <p role="status" aria-busy="true">
          Reading {PANEL}…
        </p>
      ) : null}

      {state.kind === 'problem' ? (
        <PanelProblem panel={PANEL} state={state} endpointPath={ENDPOINT_PATH} onRetry={reload} />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <div className="totals">
            <Tile label="Unresolved obligations" value={state.response.totals.unresolved_obligations} />
            <Tile label="Contracts affected" value={state.response.totals.contracts_affected} />
            <Tile
              label="Contracts with no owner"
              value={state.response.totals.contracts_without_owner}
              tone={state.response.totals.contracts_without_owner > 0 ? 'bad' : undefined}
            />
          </div>

          <ul className="qroles" aria-label="Role mappings">
            {state.response.role_mappings.map((m) => (
              <li key={m.role_pattern}>
                {m.is_mapped ? (
                  <>
                    {m.role_pattern} — mapped ({m.confirmed_targets} confirmed)
                  </>
                ) : (
                  <>
                    <strong>{m.role_pattern} — not mapped</strong> (notify exits 1 for any tier that names it)
                  </>
                )}
              </li>
            ))}
          </ul>

          <DataTable
            caption="Obligations with nobody to notify"
            columns={COLUMNS}
            rows={state.response.items}
            rowKey={(o) => o.obligation_id}
            empty={
              <p>
                <strong>Every open obligation you can see has a resolvable owner.</strong>
              </p>
            }
          />
          <CursorPager
            next_cursor={state.response.page.next_cursor}
            historyDepth={paging.historyDepth}
            onNext={() => {
              if (state.response.page.next_cursor !== null) paging.next(state.response.page.next_cursor);
            }}
            onPrevious={paging.previous}
            pageNumber={paging.page}
            returnedCount={state.response.page.returned_count}
            totalInScope={null}
          />
        </>
      ) : null}
    </section>
  );
}
